import React from 'react';
import {
  Box, Paper, Typography, Chip, Stack, Divider, Alert, Card, CardContent,
  List, ListItem, ListItemText, ListItemIcon, Accordion, AccordionSummary, AccordionDetails
} from '@mui/material';
import {
  Warning as WarningIcon,
  Person as PersonIcon,
  LocalHospital as HospitalIcon,
  Medication as MedicationIcon,
  Description as DescriptionIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { alpha, styled } from '@mui/material/styles';

const InfoCard = styled(Card)(({ theme }) => ({
  background: alpha(theme.palette.background.paper, 0.9),
  backdropFilter: 'blur(10px)',
  border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
  transition: 'all 0.3s',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[4],
  }
}));

interface ICSRData {
  // Patient Information
  patient?: {
    identifier?: string;
    age?: string;
    sex?: string;
    weight?: string;
    height?: string;
    pregnancy_status?: string;
    medical_history?: string;
  };
  
  // Adverse Event
  adverse_event?: {
    event_verbatim?: string;
    meddra_pt?: string;
    start_date?: string;
    stop_date?: string;
    duration?: string;
    outcome?: string;
    seriousness?: string;
    severity?: string;
    causality?: string;
  };
  
  // Product Information
  product?: {
    name?: string;
    generic?: string;
    dosage_form?: string;
    strength?: string;
    indication?: string;
    route?: string;
    dose?: string;
    start_date?: string;
    stop_date?: string;
    batch_number?: string;
    action_taken?: string;
  };
  
  // Reporter Information
  reporter?: {
    name?: string;
    type?: string;
    occupation?: string;
    country?: string;
    relationship?: string;
  };
  
  // Confidence and extraction metadata
  confidence_score?: number;
  extraction_method?: string;
  source?: string;
}

interface ICSRDisplayProps {
  data?: ICSRData | null;
  pharmacovigilanceData?: any;
  compact?: boolean;
}

export const ICSRDisplay: React.FC<ICSRDisplayProps> = ({ data, pharmacovigilanceData, compact = false }) => {
  // Merge data from both sources and normalize nested backend structure
  const icsrData = data || {} as ICSRData;
  const pv = pharmacovigilanceData || {} as any;

  // Normalize nested PV shape (patient_info, adverse_events[0], suspect_products[0], reporter_info)
  const normalized = {
    patient: {
      identifier: icsrData.patient?.identifier || pv.patient_identifier || pv.patient_info?.patient_identifier,
      age: icsrData.patient?.age || pv.patient_age || pv.patient_info?.age,
      sex: icsrData.patient?.sex || pv.patient_sex || pv.patient_info?.sex_gender,
      weight: icsrData.patient?.weight || pv.patient_weight || pv.patient_info?.weight,
      height: icsrData.patient?.height || pv.patient_height || pv.patient_info?.height,
      pregnancy_status: icsrData.patient?.pregnancy_status || pv.pregnancy_status || pv.patient_info?.pregnancy_status,
      medical_history: icsrData.patient?.medical_history || pv.medical_history || pv.patient_info?.medical_history,
    },
    adverse_event: {
      event_verbatim:
        icsrData.adverse_event?.event_verbatim ||
        pv.event_verbatim ||
        pv.adverse_event?.event_verbatim ||
        pv.adverse_events?.[0]?.event_term_verbatim ||
        pv.adverse_events?.[0]?.event_name,
      meddra_pt:
        icsrData.adverse_event?.meddra_pt ||
        pv.meddra_pt ||
        pv.adverse_events?.[0]?.meddra_preferred_term,
      start_date:
        icsrData.adverse_event?.start_date ||
        pv.ae_start_date ||
        pv.adverse_events?.[0]?.start_date,
      stop_date:
        icsrData.adverse_event?.stop_date ||
        pv.ae_stop_date ||
        pv.adverse_events?.[0]?.stop_date,
      duration:
        icsrData.adverse_event?.duration ||
        pv.ae_duration ||
        pv.adverse_events?.[0]?.duration,
      outcome:
        icsrData.adverse_event?.outcome ||
        pv.outcome ||
        pv.adverse_events?.[0]?.outcome,
      seriousness:
        icsrData.adverse_event?.seriousness ||
        pv.seriousness ||
        pv.adverse_events?.[0]?.seriousness,
      severity:
        icsrData.adverse_event?.severity ||
        pv.severity ||
        pv.adverse_events?.[0]?.severity,
      causality:
        icsrData.adverse_event?.causality ||
        pv.causality ||
        pv.adverse_events?.[0]?.causality_assessment,
    },
    product: {
      name:
        icsrData.product?.name ||
        pv.suspect_product_name ||
        pv.product?.name ||
        pv.suspect_products?.[0]?.drug_name_brand,
      generic:
        icsrData.product?.generic ||
        pv.suspect_product_generic ||
        pv.suspect_products?.[0]?.drug_name_generic,
      dosage_form:
        icsrData.product?.dosage_form ||
        pv.dosage_form ||
        pv.suspect_products?.[0]?.dosage_form,
      strength:
        icsrData.product?.strength ||
        pv.strength ||
        pv.suspect_products?.[0]?.strength,
      route:
        icsrData.product?.route ||
        pv.route ||
        pv.suspect_products?.[0]?.route_of_administration,
      dose:
        icsrData.product?.dose ||
        pv.dose ||
        pv.suspect_products?.[0]?.dose_per_administration ||
        pv.suspect_products?.[0]?.dose_per_day,
      indication:
        icsrData.product?.indication ||
        pv.indication ||
        pv.suspect_products?.[0]?.indication,
      start_date:
        icsrData.product?.start_date ||
        pv.product_start_date ||
        pv.suspect_products?.[0]?.start_date,
      stop_date:
        icsrData.product?.stop_date ||
        pv.product_stop_date ||
        pv.suspect_products?.[0]?.stop_date,
      batch_number:
        icsrData.product?.batch_number ||
        pv.batch_number ||
        pv.suspect_products?.[0]?.batch_number,
      action_taken:
        icsrData.product?.action_taken ||
        pv.action_taken ||
        pv.suspect_products?.[0]?.action_taken,
    },
    reporter: {
      name:
        icsrData.reporter?.name ||
        pv.reporter_name ||
        pv.reporter_info?.reporter_name,
      type:
        icsrData.reporter?.type ||
        pv.reporter_type ||
        pv.reporter_info?.reporter_type,
      occupation:
        icsrData.reporter?.occupation ||
        pv.reporter_occupation ||
        pv.reporter_info?.reporter_occupation,
      country:
        icsrData.reporter?.country ||
        pv.reporter_country ||
        pv.reporter_info?.country,
      relationship:
        icsrData.reporter?.relationship ||
        pv.reporter_relationship ||
        pv.reporter_info?.relationship_to_patient,
    },
    confidence_score: icsrData.confidence_score || pv.confidence_score || 0,
  };

  const hasPatientInfo = Boolean(
    icsrData.patient ||
    normalized.patient.identifier ||
    normalized.patient.age ||
    normalized.patient.sex
  );
  const hasAEInfo = Boolean(
    icsrData.adverse_event ||
    normalized.adverse_event.event_verbatim ||
    normalized.adverse_event.meddra_pt
  );
  const hasProductInfo = Boolean(
    icsrData.product ||
    normalized.product.name ||
    normalized.product.generic
  );
  const hasReporterInfo = Boolean(
    icsrData.reporter ||
    normalized.reporter.name ||
    normalized.reporter.type
  );
  
  const confidenceScore = normalized.confidence_score;
  
  if (!hasPatientInfo && !hasAEInfo && !hasProductInfo && !hasReporterInfo) {
    return (
      <Alert severity="info" sx={{ mt: 2 }}>
        No ICSR information extracted from this article
      </Alert>
    );
  }

  const getSeverityColor = (severity?: string) => {
    switch (severity?.toLowerCase()) {
      case 'serious': return 'error';
      case 'severe': return 'error';
      case 'moderate': return 'warning';
      case 'mild': return 'info';
      default: return 'default';
    }
  };

  if (compact) {
    return (
      <Box sx={{ mt: 2 }}>
        <Stack spacing={1}>
          {hasAEInfo && (
            <Alert severity="warning" icon={<WarningIcon />}>
              <Typography variant="subtitle2">Adverse Event Detected</Typography>
              <Typography variant="body2">
                {icsrData.adverse_event?.event_verbatim || normalized.adverse_event.event_verbatim || 'Event details available'}
              </Typography>
              {confidenceScore > 0 && (
                <Chip 
                  size="small" 
                  label={`${(confidenceScore * 100).toFixed(0)}% confidence`}
                  sx={{ mt: 1 }}
                />
              )}
            </Alert>
          )}
          
          {hasProductInfo && (
            <Stack direction="row" spacing={1} alignItems="center">
              <MedicationIcon fontSize="small" color="primary" />
              <Typography variant="body2">
                Product: {icsrData.product?.name || normalized.product.name || 'Product identified'}
              </Typography>
            </Stack>
          )}
          
          {hasPatientInfo && (
            <Stack direction="row" spacing={1} alignItems="center">
              <PersonIcon fontSize="small" color="action" />
              <Typography variant="body2">
                Patient: {icsrData.patient?.age || normalized.patient.age || 'Age N/A'}, 
                {' '}{icsrData.patient?.sex || normalized.patient.sex || 'Sex N/A'}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HospitalIcon /> ICSR Information
        {confidenceScore > 0 && (
          <Chip 
            size="small" 
            label={`${(confidenceScore * 100).toFixed(0)}% confidence`}
            color={confidenceScore > 0.7 ? 'success' : 'warning'}
          />
        )}
      </Typography>

      <Stack spacing={2}>
        {/* Adverse Event Information */}
        {hasAEInfo && (
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <WarningIcon color="error" />
                <Typography variant="subtitle1">Adverse Event</Typography>
                {(icsrData.adverse_event?.severity || normalized.adverse_event.severity) && (
                  <Chip 
                    size="small" 
                    label={icsrData.adverse_event?.severity || normalized.adverse_event.severity}
                    color={getSeverityColor(icsrData.adverse_event?.severity || normalized.adverse_event.severity)}
                  />
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box sx={{ gridColumn: '1 / -1' }}>
                  <Typography variant="subtitle2" color="text.secondary">Event Description</Typography>
                  <Typography variant="body2">
                    {icsrData.adverse_event?.event_verbatim || normalized.adverse_event.event_verbatim || 'Not specified'}
                  </Typography>
                </Box>
                {/* Render multiple detected events when available (support various backend shapes) */}
                {(() => {
                  const terms = (
                    pv?.event_terms ||
                    pv?.adverse_event?.event_terms ||
                    pv?.extracted_fields?.event_terms ||
                    pv?.extractedFields?.event_terms ||
                    pv?.adverse_events?.[0]?.event_terms ||
                    []
                  ) as string[];
                  return Array.isArray(terms) && terms.length > 0 ? (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="subtitle2" color="text.secondary">All Detected Events</Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                        {terms.map((term: string, idx: number) => (
                          <Chip key={`${term}-${idx}`} size="small" label={term} />
                        ))}
                      </Stack>
                    </Box>
                  ) : null;
                })()}
                {(icsrData.adverse_event?.meddra_pt || normalized.adverse_event.meddra_pt) && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="subtitle2" color="text.secondary">MedDRA PT</Typography>
                    <Typography variant="body2">
                      {icsrData.adverse_event?.meddra_pt || normalized.adverse_event.meddra_pt}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Start Date</Typography>
                  <Typography variant="body2">
                    {icsrData.adverse_event?.start_date || normalized.adverse_event.start_date || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Outcome</Typography>
                  <Typography variant="body2">
                    {icsrData.adverse_event?.outcome || normalized.adverse_event.outcome || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Seriousness</Typography>
                  <Typography variant="body2">
                    {icsrData.adverse_event?.seriousness || normalized.adverse_event.seriousness || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Causality</Typography>
                  <Typography variant="body2">
                    {icsrData.adverse_event?.causality || normalized.adverse_event.causality || 'Not specified'}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Product Information */}
        {hasProductInfo && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <MedicationIcon color="primary" />
                <Typography variant="subtitle1">Product Information</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Product Name</Typography>
                  <Typography variant="body2">
                    {icsrData.product?.name || normalized.product.name || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Generic Name</Typography>
                  <Typography variant="body2">
                    {icsrData.product?.generic || normalized.product.generic || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Dosage Form</Typography>
                  <Typography variant="body2">
                    {icsrData.product?.dosage_form || normalized.product.dosage_form || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Strength</Typography>
                  <Typography variant="body2">
                    {icsrData.product?.strength || normalized.product.strength || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Route</Typography>
                  <Typography variant="body2">
                    {icsrData.product?.route || normalized.product.route || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Dose</Typography>
                  <Typography variant="body2">
                    {icsrData.product?.dose || normalized.product.dose || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Indication</Typography>
                  <Typography variant="body2">
                    {icsrData.product?.indication || normalized.product.indication || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Action Taken</Typography>
                  <Typography variant="body2">
                    {icsrData.product?.action_taken || normalized.product.action_taken || 'Not specified'}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Patient Information */}
        {hasPatientInfo && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonIcon color="action" />
                <Typography variant="subtitle1">Patient Information</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Patient ID</Typography>
                  <Typography variant="body2">
                    {icsrData.patient?.identifier || normalized.patient.identifier || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Age</Typography>
                  <Typography variant="body2">
                    {icsrData.patient?.age || normalized.patient.age || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Sex</Typography>
                  <Typography variant="body2">
                    {icsrData.patient?.sex || normalized.patient.sex || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Weight</Typography>
                  <Typography variant="body2">
                    {icsrData.patient?.weight || normalized.patient.weight || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Height</Typography>
                  <Typography variant="body2">
                    {icsrData.patient?.height || normalized.patient.height || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Pregnancy Status</Typography>
                  <Typography variant="body2">
                    {icsrData.patient?.pregnancy_status || normalized.patient.pregnancy_status || 'Not specified'}
                  </Typography>
                </Box>
                {(icsrData.patient?.medical_history || normalized.patient.medical_history) && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography variant="subtitle2" color="text.secondary">Medical History</Typography>
                    <Typography variant="body2">
                      {icsrData.patient?.medical_history || normalized.patient.medical_history}
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        )}

        {/* Reporter Information */}
        {hasReporterInfo && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center">
                <DescriptionIcon color="action" />
                <Typography variant="subtitle1">Reporter Information</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Reporter Name</Typography>
                  <Typography variant="body2">
                    {icsrData.reporter?.name || normalized.reporter.name || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Reporter Type</Typography>
                  <Typography variant="body2">
                    {icsrData.reporter?.type || normalized.reporter.type || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Occupation</Typography>
                  <Typography variant="body2">
                    {icsrData.reporter?.occupation || normalized.reporter.occupation || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Country</Typography>
                  <Typography variant="body2">
                    {icsrData.reporter?.country || normalized.reporter.country || 'Not specified'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">Relationship to Patient</Typography>
                  <Typography variant="body2">
                    {icsrData.reporter?.relationship || normalized.reporter.relationship || 'Not specified'}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
      </Stack>
    </Box>
  );
};

export default ICSRDisplay;
